import { useState } from 'react'
import { NavLink } from 'react-router-dom'

interface NavItem {
  to: string
  label: string
  end?: boolean
}

interface NavSection {
  heading?: string
  items: NavItem[]
  collapsible?: boolean
  defaultCollapsed?: boolean
}

const sections: NavSection[] = [
  {
    items: [
      { to: '/admin-alerts/log', label: 'Log', end: true },
      { to: '/admin-alerts/phases', label: 'Alert Phases' },
    ],
  },
  {
    heading: 'Catch-All',
    items: [
      { to: '/admin-alerts/recipients', label: 'Recipients' },
      { to: '/admin-alerts/subscriptions', label: 'Who Gets What' },
      { to: '/admin-alerts/settings', label: 'Settings' },
      { to: '/admin-alerts/mutes', label: 'Device Mutes' },
    ],
  },
  {
    heading: 'Test Alerts',
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { to: '/admin-alerts/test/direct-email', label: 'Direct Email' },
      { to: '/admin-alerts/test/direct-sms', label: 'Direct SMS' },
      { to: '/admin-alerts/test/pipeline-email', label: 'Pipeline Email' },
      { to: '/admin-alerts/test/pipeline-sms', label: 'Pipeline SMS' },
    ],
  },
]

function NavItems({ items }: { items: NavItem[] }) {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `flex items-center px-4 py-2 rounded-lg transition whitespace-nowrap ${
              isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`
          }
        >
          <span className="flex-1">{item.label}</span>
        </NavLink>
      ))}
    </>
  )
}

function CollapsibleSection({ section }: { section: NavSection }) {
  const [collapsed, setCollapsed] = useState(section.defaultCollapsed ?? false)
  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
      >
        <span className="flex-1 text-left">{section.heading}</span>
        <span className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}>▸</span>
      </button>
      {!collapsed && <NavItems items={section.items} />}
    </div>
  )
}

export default function AlertsNav() {
  return (
    <nav className="space-y-4 p-4">
      {sections.map((section, idx) =>
        section.collapsible ? (
          <CollapsibleSection key={idx} section={section} />
        ) : (
          <div key={idx} className="space-y-1">
            {section.heading && (
              <p className="px-4 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{section.heading}</p>
            )}
            <NavItems items={section.items} />
          </div>
        )
      )}
    </nav>
  )
}
